import { useState, type FormEvent } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import type { PlacementFailure } from '@blesk/contracts';
import { ApiRequestError } from '../../shared/api';
import { useLogin } from '../../shared/session';

/**
 * §15.2, §15.3 ТЗ. Вход по локальной учётной записи.
 *
 * Во встроенном режиме сюда попадают только те, кому не досталось место
 * размещения VibeCode: обмен placement-контекста делает сервер, браузер
 * ключей и токенов не видит (§15.1).
 */

/**
 * §15.1: почему человека, пришедшего из портала, вернули на форму входа.
 *
 * Причину называет сервер параметром адреса — сам он ответить страницей не
 * может: его ответ читает браузер внутри фрейма портала. Текст живёт здесь,
 * потому что он для человека, а не для журнала: «шлюз недоступен» и «вас нет
 * в системе» требуют разных действий, и молчаливая форма входа не подсказала
 * бы ни одного.
 */
const PLACEMENT_REASONS: Record<PlacementFailure, string> = {
  'not-registered':
    'Вы вошли из портала, но не заведены в системе. Обратитесь к администратору сервиса.',
  rejected: 'Портал не подтвердил, кто открыл панель. Войдите по логину и паролю.',
  unavailable: 'Вход через портал сейчас недоступен. Войдите по логину и паролю.',
  'too-many': 'Слишком много попыток входа. Повторите позже или войдите по логину и паролю.',
};

export function LoginPage() {
  const { next, placement } = useSearch({ from: '/login' });
  // Параметр приходит из адресной строки: незнакомое значение — молчание, не текст
  const placementReason =
    placement && placement in PLACEMENT_REASONS
      ? PLACEMENT_REASONS[placement as PlacementFailure]
      : undefined;
  const router = useRouter();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Неудачный вход — обычный исход, а не исключение: ошибку показывает форма
    login.mutate(
      { email, password },
      // Адрес, с которого человека вернули на вход, восстанавливается как есть
      { onSuccess: () => router.history.push(next ?? '/') },
    );
  };

  return (
    <main className="centered">
      <form className="card" onSubmit={onSubmit}>
        <div className="card__brand">
          <span className="brand__mark" aria-hidden="true">
            Б
          </span>
          <div>
            <h1>Вход</h1>
            <p className="muted card__subtitle">Контроль качества и обращений</p>
          </div>
        </div>

        {placementReason && (
          <p className="notice" role="status">
            {placementReason}
          </p>
        )}

        <label htmlFor="email">Электронная почта</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="password">Пароль</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {login.isError && (
          <p className="error" role="alert">
            {/* §18: наружу уходит безопасное сообщение, без подсказки «такой почты нет» */}
            {login.error instanceof ApiRequestError
              ? login.error.payload.message
              : 'Не удалось войти. Повторите попытку.'}
          </p>
        )}

        <button type="submit" disabled={login.isPending}>
          {login.isPending ? 'Проверяем…' : 'Войти'}
        </button>
      </form>
    </main>
  );
}
